import { useState } from "react";
import { Alert, Button, Card, Text } from "@marekl/adminer/components";
import { apiErrorMessage } from "@marekl/adminer/api";
import { useT } from "@marekl/adminer/hooks";
import { DocumentConsentList } from "../../../components/DocumentConsentList";
import { allTicked } from "../../../lib/documentConsent";
import {
  documentsMovedCode,
  useConsentSeasonDocuments,
  type TeamSeasonDocument,
} from "../../../lib/queries/seasonDocuments";
import type { ID } from "../../../lib/id";

/**
 * „Souhlas s dokumenty" on the team page: a registered club agrees to the
 * season documents it has not yet (one added or replaced since it
 * registered, or a club registered by approval), with no registration
 * submit (seasondoc.Consent). Its own box rather than a button in the state
 * bar: it is something to read and tick, not a status. Rendered only while
 * something is pending; once agreed the badge in the state bar says so.
 */
export function DocumentConsentCard({
  teamId,
  documents,
  agrees,
  leaderHasLogin = true,
}: {
  teamId: ID;
  /** The pending documents; the caller renders nothing when empty. */
  documents: TeamSeasonDocument[];
  /** The viewer is the team's leader (agreesForTeam). Anybody else reads
   * what is pending and is told who confirms it; the server refuses them
   * (seasondoc.ErrLeaderOnly). */
  agrees: boolean;
  /** Whether the leader has an account to agree with. Without one nobody
   * can, so an administrator is told to create it. */
  leaderHasLogin?: boolean;
}) {
  const t = useT();
  const [ticked, setTicked] = useState<ReadonlySet<ID>>(() => new Set());
  const consent = useConsentSeasonDocuments(teamId);
  const submit = () =>
    consent.mutate(
      documents.map((d) => d.version_id),
      {
        onSuccess: () => setTicked(new Set()),
        // A ticked version was replaced: what was ticked is not in force.
        onError: (err) => {
          if (documentsMovedCode(err) === "documents_changed") setTicked(new Set());
        },
      },
    );
  if (!agrees) {
    return (
      <Card>
        <Card.Header
          title="Souhlas s dokumenty"
          subtitle="Tým zatím neodsouhlasil tyto dokumenty sezóny."
        />
        <Card.Content>
          <ul>
            {documents.map((d) => (
              <li key={d.version_id}>{d.name}</li>
            ))}
          </ul>
          <Text variant="muted">
            {leaderHasLogin
              ? "Souhlas potvrzuje vedoucí týmu."
              : "Souhlas potvrzuje vedoucí týmu, který zatím nemá účet. Vytvořte mu ho v kartě Vedoucí."}
          </Text>
        </Card.Content>
      </Card>
    );
  }
  return (
    <Card>
      <Card.Header
        title="Souhlas s dokumenty"
        subtitle="Tým zatím neodsouhlasil tyto dokumenty sezóny."
      />
      <Card.Content>
        {consent.error && (
          <Alert variant="err">{apiErrorMessage(consent.error, t)}</Alert>
        )}
        <DocumentConsentList
          documents={documents}
          ticked={ticked}
          onToggle={(versionId, checked) =>
            setTicked((prev) => {
              const next = new Set(prev);
              if (checked) next.add(versionId);
              else next.delete(versionId);
              return next;
            })
          }
          missingMessage="Souhlas lze potvrdit až po zaškrtnutí všech dokumentů."
          disabled={consent.isPending}
        />
      </Card.Content>
      <Card.Footer>
        <Button
          variant="primary"
          loading={consent.isPending}
          disabled={!allTicked(documents, ticked)}
          onClick={submit}
        >
          Potvrdit souhlas
        </Button>
      </Card.Footer>
    </Card>
  );
}
